import { useState, useCallback, useRef, useEffect } from 'react';
import superjson from 'superjson';
import { stopAllInworldTTS } from './useInworldTTS';

// Kitten TTS voices
export const KITTEN_VOICES = [
  { id: 'Bella', name: 'Bella', gender: 'female', style: 'warm' },
  { id: 'Jasper', name: 'Jasper', gender: 'male', style: 'professional' },
  { id: 'Luna', name: 'Luna', gender: 'female', style: 'gentle' },
  { id: 'Bruno', name: 'Bruno', gender: 'male', style: 'deep' },
  { id: 'Rosie', name: 'Rosie', gender: 'female', style: 'cheerful' },
  { id: 'Hugo', name: 'Hugo', gender: 'male', style: 'casual' },
  { id: 'Kiki', name: 'Kiki', gender: 'female', style: 'energetic' },
  { id: 'Leo', name: 'Leo', gender: 'male', style: 'friendly' },
] as const;

export type KittenVoiceId = (typeof KITTEN_VOICES)[number]['id'];

interface KittenTTSOptions {
  voice?: KittenVoiceId;
  speed?: number;
}

interface UseKittenTTSReturn {
  speak: (text: string, options?: KittenTTSOptions) => Promise<void>;
  stop: () => void;
  isPlaying: boolean;
  isSpeaking: boolean;
  error: string | null;
}

// Global reference to track the active Kitten TTS instance
let activeKittenTTSInstance: { stop: () => void } | null = null;

/**
 * Stop any globally active Kitten TTS instance
 */
export function stopAllKittenTTS() {
  if (activeKittenTTSInstance) {
    activeKittenTTSInstance.stop();
    activeKittenTTSInstance = null;
  }
}

// Global cooldown after API errors
let kittenErrorCooldownUntil = 0;
const KITTEN_ERROR_COOLDOWN_MS = 30_000;
const KITTEN_UNAVAILABLE_COOLDOWN_MS = 5 * 60_000; // 5 min if server not configured
let kittenInFlight = false;

export function useKittenTTS(): UseKittenTTSReturn {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<AudioBufferSourceNode | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const stop = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    if (sourceNodeRef.current) {
      try {
        sourceNodeRef.current.stop();
      } catch {
        // Ignore if already stopped
      }
      sourceNodeRef.current = null;
    }

    setIsPlaying(false);
    setIsSpeaking(false);

    if (activeKittenTTSInstance?.stop === stop) {
      activeKittenTTSInstance = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stop();
    };
  }, [stop]);

  const speak = useCallback(
    async (text: string, options: KittenTTSOptions = {}) => {
      // Skip if in cooldown
      if (Date.now() < kittenErrorCooldownUntil) return;

      // Prevent concurrent calls
      if (kittenInFlight) return;
      kittenInFlight = true;

      // Stop any existing TTS playback (both Kitten and Inworld)
      stopAllKittenTTS();
      stopAllInworldTTS();

      if (!text.trim()) {
        kittenInFlight = false;
        return;
      }

      setError(null);
      setIsSpeaking(true);

      const { voice = 'Jasper', speed = 1.0 } = options;

      try {
        abortControllerRef.current = new AbortController();

        const response = await fetch('/_api/tts/kitten', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: superjson.stringify({ text, voice, speed }),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          const statusCode = response.status;
          if (statusCode === 503) {
            // Server not configured — long cooldown
            kittenErrorCooldownUntil = Date.now() + KITTEN_UNAVAILABLE_COOLDOWN_MS;
            console.warn('[KittenTTS] Not configured — disabled for 5 minutes');
          } else {
            kittenErrorCooldownUntil = Date.now() + KITTEN_ERROR_COOLDOWN_MS;
          }
          kittenInFlight = false;
          setError(`Kitten TTS failed: ${statusCode}`);
          setIsSpeaking(false);
          return;
        }

        const audioData = await response.arrayBuffer();

        if (!audioContextRef.current) {
          audioContextRef.current = new AudioContext();
        }

        const audioContext = audioContextRef.current;

        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        const audioBuffer = await audioContext.decodeAudioData(audioData);

        const sourceNode = audioContext.createBufferSource();
        sourceNode.buffer = audioBuffer;
        sourceNode.connect(audioContext.destination);

        sourceNode.onended = () => {
          setIsPlaying(false);
          setIsSpeaking(false);
          sourceNodeRef.current = null;
          if (activeKittenTTSInstance?.stop === stop) {
            activeKittenTTSInstance = null;
          }
        };

        activeKittenTTSInstance = { stop };

        sourceNodeRef.current = sourceNode;
        setIsPlaying(true);
        sourceNode.start();
        kittenInFlight = false;
      } catch (err) {
        kittenInFlight = false;
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }
        console.error('[KittenTTS] Error:', err);
        kittenErrorCooldownUntil = Date.now() + KITTEN_ERROR_COOLDOWN_MS;
        setError(err instanceof Error ? err.message : 'TTS failed');
        setIsSpeaking(false);
      }
    },
    [stop],
  );

  return {
    speak,
    stop,
    isPlaying,
    isSpeaking,
    error,
  };
}

/**
 * Get a consistent Kitten voice for a persona based on stable hash
 */
export function getKittenVoiceForPersona(
  personaId: number | string,
  personaName?: string,
): KittenVoiceId {
  let hash = 0;
  const str = String(personaId || personaName || 'default');
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  const voiceIndex = Math.abs(hash) % KITTEN_VOICES.length;
  return KITTEN_VOICES[voiceIndex].id;
}
